import {
  EyeOff,
  Eye,
  Heart,
  BatteryLow,
  Battery,
  Skull,
  Link,
  Link2,
  CircleMinus,
  CircleOff,
  Lock,
  Mountain,
  Droplet,
  Droplets,
  ArrowDownToLine,
  ArrowLeftRight,
  Anchor,
  Star,
  Moon,
  ShieldOff,
  ShieldAlert,
  Flame,
  Layers,
  Snowflake,
  Crosshair,
  Wind,
  Wrench,
  HelpCircle,
  Sparkles,
  VolumeX,
  ZapOff,
  Zap,
  AlertCircle,
  AlertTriangle,
  Frown,
  Meh,
  MapPin,
  Pill,
  Package,
  Target,
  RotateCcw,
  Brain,
  Activity,
  Clock,
  Search,
  Swords,
  Sliders,
} from 'lucide-react';

/**
 * Condition definitions per TTRPG system.
 * Each entry: { id, label, icon (Lucide component), color }
 */

const DND5E_CONDITIONS = [
  { id: 'blinded',       label: 'Blinded',       icon: EyeOff,          color: '#9b9b9b' },
  { id: 'charmed',       label: 'Charmed',        icon: Heart,           color: '#e882b8' },
  { id: 'exhausted',     label: 'Exhausted',      icon: BatteryLow,      color: '#e9a23b' },
  { id: 'frightened',    label: 'Frightened',     icon: Skull,           color: '#8a7fbf' },
  { id: 'grappled',      label: 'Grappled',       icon: Link2,           color: '#6cbfbf' },
  { id: 'incapacitated', label: 'Incapacitated',  icon: CircleMinus,     color: '#e94f37' },
  { id: 'paralyzed',     label: 'Paralyzed',      icon: Lock,            color: '#4fa8e9' },
  { id: 'petrified',     label: 'Petrified',      icon: Mountain,        color: '#aaaaaa' },
  { id: 'poisoned',      label: 'Poisoned',       icon: Droplets,        color: '#5dbf5d' },
  { id: 'prone',         label: 'Prone',          icon: ArrowDownToLine, color: '#c8a05a' },
  { id: 'restrained',    label: 'Restrained',     icon: Anchor,          color: '#c46b2d' },
  { id: 'stunned',       label: 'Stunned',        icon: Star,            color: '#f5e642' },
  { id: 'unconscious',   label: 'Unconscious',    icon: Moon,            color: '#5a5a8a' },
];

const DAGGERHEART_CONDITIONS = [
  { id: 'hidden',      label: 'Hidden',      icon: EyeOff,          color: '#9b9b9b' },
  { id: 'restrained',  label: 'Restrained',  icon: Anchor,          color: '#c46b2d' },
  { id: 'vulnerable',  label: 'Vulnerable',  icon: ShieldOff,       color: '#e94f37' },
  { id: 'poisoned',    label: 'Poisoned',    icon: Droplets,        color: '#5dbf5d' },
  { id: 'cursed',      label: 'Cursed',      icon: Skull,           color: '#8a7fbf' },
  { id: 'ignited',     label: 'Ignited',     icon: Flame,           color: '#e9773b' },
  { id: 'dazed',       label: 'Dazed',       icon: Layers,          color: '#e882b8' },
  { id: 'chilled',     label: 'Chilled',     icon: Snowflake,       color: '#4fa8e9' },
  { id: 'trapped',     label: 'Trapped',     icon: Lock,            color: '#c8a05a' },
  { id: 'marked',      label: 'Marked',      icon: Crosshair,       color: '#e94f37' },
  { id: 'enveloped',   label: 'Enveloped',   icon: Wind,            color: '#6cbfbf' },
];

const PF1E_CONDITIONS = [
  { id: 'bleed',          label: 'Bleed',          icon: Droplet,         color: '#cc2222' },
  { id: 'blinded',        label: 'Blinded',        icon: EyeOff,          color: '#9b9b9b' },
  { id: 'broken',         label: 'Broken',         icon: Wrench,          color: '#666666' },
  { id: 'confused',       label: 'Confused',       icon: HelpCircle,      color: '#cc44cc' },
  { id: 'cowering',       label: 'Cowering',       icon: ShieldAlert,     color: '#7744aa' },
  { id: 'dazed',          label: 'Dazed',          icon: Layers,          color: '#e882b8' },
  { id: 'dazzled',        label: 'Dazzled',        icon: Sparkles,        color: '#ddd040' },
  { id: 'deafened',       label: 'Deafened',       icon: VolumeX,         color: '#8a6a4a' },
  { id: 'disabled',       label: 'Disabled',       icon: CircleOff,       color: '#882222' },
  { id: 'energy_drained', label: 'Energy Drained', icon: Battery,         color: '#224488' },
  { id: 'entangled',      label: 'Entangled',      icon: Link,            color: '#6cbfbf' },
  { id: 'exhausted',      label: 'Exhausted',      icon: BatteryLow,      color: '#e9a23b' },
  { id: 'fascinated',     label: 'Fascinated',     icon: Eye,             color: '#6080cc' },
  { id: 'fatigued',       label: 'Fatigued',       icon: ZapOff,          color: '#bb7722' },
  { id: 'flat_footed',    label: 'Flat-Footed',    icon: AlertCircle,     color: '#999955' },
  { id: 'frightened',     label: 'Frightened',     icon: Skull,           color: '#8a7fbf' },
  { id: 'grappled',       label: 'Grappled',       icon: Link2,           color: '#6cbfbf' },
  { id: 'helpless',       label: 'Helpless',       icon: AlertCircle,     color: '#774422' },
  { id: 'incorporeal',    label: 'Incorporeal',    icon: Sparkles,        color: '#cc88cc' },
  { id: 'invisible',      label: 'Invisible',      icon: EyeOff,          color: '#aaaaaa' },
  { id: 'nauseated',      label: 'Nauseated',      icon: Frown,           color: '#887744' },
  { id: 'panicked',       label: 'Panicked',       icon: AlertTriangle,   color: '#ff4444' },
  { id: 'paralyzed',      label: 'Paralyzed',      icon: Lock,            color: '#4fa8e9' },
  { id: 'petrified',      label: 'Petrified',      icon: Mountain,        color: '#aaaaaa' },
  { id: 'pinned',         label: 'Pinned',         icon: MapPin,          color: '#337766' },
  { id: 'prone',          label: 'Prone',          icon: ArrowDownToLine, color: '#c8a05a' },
  { id: 'shaken',         label: 'Shaken',         icon: Zap,             color: '#9966bb' },
  { id: 'sickened',       label: 'Sickened',       icon: Pill,            color: '#558833' },
  { id: 'staggered',      label: 'Staggered',      icon: ArrowLeftRight,  color: '#cc8833' },
  { id: 'stunned',        label: 'Stunned',        icon: Star,            color: '#f5e642' },
];

const SF1E_CONDITIONS = [
  { id: 'asleep',        label: 'Asleep',        icon: Moon,            color: '#3344aa' },
  { id: 'bleeding',      label: 'Bleeding',      icon: Droplet,         color: '#cc2222' },
  { id: 'blinded',       label: 'Blinded',       icon: EyeOff,          color: '#9b9b9b' },
  { id: 'broken',        label: 'Broken',        icon: Wrench,          color: '#666666' },
  { id: 'burning',       label: 'Burning',       icon: Flame,           color: '#e9773b' },
  { id: 'confused',      label: 'Confused',      icon: HelpCircle,      color: '#cc44cc' },
  { id: 'cowering',      label: 'Cowering',      icon: ShieldAlert,     color: '#7744aa' },
  { id: 'dazed',         label: 'Dazed',         icon: Layers,          color: '#e882b8' },
  { id: 'dazzled',       label: 'Dazzled',       icon: Sparkles,        color: '#ddd040' },
  { id: 'deafened',      label: 'Deafened',      icon: VolumeX,         color: '#8a6a4a' },
  { id: 'encumbered',    label: 'Encumbered',    icon: Package,         color: '#885533' },
  { id: 'entangled',     label: 'Entangled',     icon: Link,            color: '#6cbfbf' },
  { id: 'exhausted',     label: 'Exhausted',     icon: BatteryLow,      color: '#e9a23b' },
  { id: 'fascinated',    label: 'Fascinated',    icon: Eye,             color: '#6080cc' },
  { id: 'fatigued',      label: 'Fatigued',      icon: ZapOff,          color: '#bb7722' },
  { id: 'flat_footed',   label: 'Flat-Footed',   icon: AlertCircle,     color: '#999955' },
  { id: 'frightened',    label: 'Frightened',    icon: Skull,           color: '#8a7fbf' },
  { id: 'grappled',      label: 'Grappled',      icon: Link2,           color: '#6cbfbf' },
  { id: 'helpless',      label: 'Helpless',      icon: AlertCircle,     color: '#774422' },
  { id: 'nauseated',     label: 'Nauseated',     icon: Frown,           color: '#887744' },
  { id: 'off_kilter',    label: 'Off-Kilter',    icon: RotateCcw,       color: '#cc8844' },
  { id: 'off_target',    label: 'Off-Target',    icon: Target,          color: '#cc5555' },
  { id: 'overburdened',  label: 'Overburdened',  icon: Package,         color: '#664422' },
  { id: 'panicked',      label: 'Panicked',      icon: AlertTriangle,   color: '#ff4444' },
  { id: 'paralyzed',     label: 'Paralyzed',     icon: Lock,            color: '#4fa8e9' },
  { id: 'pinned',        label: 'Pinned',        icon: MapPin,          color: '#337766' },
  { id: 'prone',         label: 'Prone',         icon: ArrowDownToLine, color: '#c8a05a' },
  { id: 'shaken',        label: 'Shaken',        icon: Zap,             color: '#9966bb' },
  { id: 'sickened',      label: 'Sickened',      icon: Pill,            color: '#558833' },
  { id: 'staggered',     label: 'Staggered',     icon: ArrowLeftRight,  color: '#cc8833' },
  { id: 'stunned',       label: 'Stunned',       icon: Star,            color: '#f5e642' },
];

const PF2E_SF2E_CONDITIONS = [
  { id: 'blinded',           label: 'Blinded',           icon: EyeOff,          color: '#9b9b9b' },
  { id: 'broken',            label: 'Broken',            icon: Wrench,          color: '#666666' },
  { id: 'clumsy',            label: 'Clumsy',            icon: ArrowLeftRight,  color: '#cc8833' },
  { id: 'concealed',         label: 'Concealed',         icon: Eye,             color: '#7777aa' },
  { id: 'confused',          label: 'Confused',          icon: HelpCircle,      color: '#cc44cc' },
  { id: 'controlled',        label: 'Controlled',        icon: Sliders,         color: '#44aacc' },
  { id: 'dazzled',           label: 'Dazzled',           icon: Sparkles,        color: '#ddd040' },
  { id: 'deafened',          label: 'Deafened',          icon: VolumeX,         color: '#8a6a4a' },
  { id: 'doomed',            label: 'Doomed',            icon: AlertTriangle,   color: '#880000' },
  { id: 'drained',           label: 'Drained',           icon: Battery,         color: '#333388' },
  { id: 'encumbered',        label: 'Encumbered',        icon: Package,         color: '#885533' },
  { id: 'enfeebled',         label: 'Enfeebled',         icon: ZapOff,          color: '#668866' },
  { id: 'fascinated',        label: 'Fascinated',        icon: Eye,             color: '#6080cc' },
  { id: 'fatigued',          label: 'Fatigued',          icon: ZapOff,          color: '#bb7722' },
  { id: 'flat_footed',       label: 'Flat-Footed',       icon: AlertCircle,     color: '#999955' },
  { id: 'fleeing',           label: 'Fleeing',           icon: Wind,            color: '#55aadd' },
  { id: 'friendly',          label: 'Friendly',          icon: Heart,           color: '#ff88bb' },
  { id: 'frightened',        label: 'Frightened',        icon: Skull,           color: '#8a7fbf' },
  { id: 'grabbed',           label: 'Grabbed',           icon: Link2,           color: '#6cbfbf' },
  { id: 'helpful',           label: 'Helpful',           icon: Heart,           color: '#ffaacc' },
  { id: 'hidden',            label: 'Hidden',            icon: EyeOff,          color: '#9b9b9b' },
  { id: 'hostile',           label: 'Hostile',           icon: Swords,          color: '#cc1111' },
  { id: 'immobilized',       label: 'Immobilized',       icon: Anchor,          color: '#225544' },
  { id: 'indifferent',       label: 'Indifferent',       icon: Meh,             color: '#888888' },
  { id: 'invisible',         label: 'Invisible',         icon: EyeOff,          color: '#aaaaaa' },
  { id: 'observed',          label: 'Observed',          icon: Eye,             color: '#88cc88' },
  { id: 'paralyzed',         label: 'Paralyzed',         icon: Lock,            color: '#4fa8e9' },
  { id: 'persistent_damage', label: 'Persistent Damage', icon: Activity,        color: '#dd4444' },
  { id: 'petrified',         label: 'Petrified',         icon: Mountain,        color: '#aaaaaa' },
  { id: 'prone',             label: 'Prone',             icon: ArrowDownToLine, color: '#c8a05a' },
  { id: 'quickened',         label: 'Quickened',         icon: Zap,             color: '#ffdd00' },
  { id: 'restrained',        label: 'Restrained',        icon: Anchor,          color: '#c46b2d' },
  { id: 'sickened',          label: 'Sickened',          icon: Pill,            color: '#558833' },
  { id: 'slowed',            label: 'Slowed',            icon: Clock,           color: '#555555' },
  { id: 'stunned',           label: 'Stunned',           icon: Star,            color: '#f5e642' },
  { id: 'stupefied',         label: 'Stupefied',         icon: Brain,           color: '#4444aa' },
  { id: 'unconscious',       label: 'Unconscious',       icon: Moon,            color: '#5a5a8a' },
  { id: 'undetected',        label: 'Undetected',        icon: Search,          color: '#aaaaaa' },
  { id: 'unfriendly',        label: 'Unfriendly',        icon: Frown,           color: '#cc4466' },
  { id: 'unnoticed',         label: 'Unnoticed',         icon: EyeOff,          color: '#666666' },
  { id: 'wounded',           label: 'Wounded',           icon: Activity,        color: '#aa2222' },
];

const COC7E_CONDITIONS = [
  { id: 'temp_insane', label: 'Temporarily Insane', icon: Brain,  color: '#661166' },
  { id: 'insane',      label: 'Insane',              icon: Brain,  color: '#440044' },
  { id: 'stunned',     label: 'Stunned',             icon: Star,   color: '#f5e642' },
  { id: 'impaled',     label: 'Impaled',             icon: Swords, color: '#880000' },
];

export const SYSTEM_CONDITIONS = {
  dnd5e:       DND5E_CONDITIONS,
  trialment:   DND5E_CONDITIONS,   // same conditions as D&D 5e
  cota:        [],                 // no conditions
  daggerheart: DAGGERHEART_CONDITIONS,
  pf1e:        PF1E_CONDITIONS,
  sf1e:        SF1E_CONDITIONS,
  pf2e:        PF2E_SF2E_CONDITIONS,
  sf2e:        PF2E_SF2E_CONDITIONS, // same conditions as PF2e
  coc7e:       COC7E_CONDITIONS,
};

/** Returns the condition list for a given system id, or [] if none. */
export function getConditionsForSystem(systemId) {
  return SYSTEM_CONDITIONS[systemId] || [];
}

/** Looks up a single condition definition by system + condition id. */
export function findCondition(systemId, conditionId) {
  return (SYSTEM_CONDITIONS[systemId] || []).find((c) => c.id === conditionId) || null;
}
